/**
 * RFC 7807 problem details, as returned by the API for every error response.
 * Backend architecture §5: errors are problem+json, never bare strings.
 */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Field-level violations, keyed by JSON pointer or field name. */
  errors?: Record<string, string[]>;
  /**
   * The response body exactly as the server sent it, whatever shape it had.
   *
   * Most of this app's errors are problem+json and every field above already
   * reads them; this exists for the handful that are not. `POST
   * .../cases/{id}/versions`'s 409, for one, answers with its own
   * `VersionConflictOut` (`{ latestVersionNo }`) rather than a problem body —
   * a caller that needs that number reads it from here rather than losing it
   * the moment the shared error middleware turns every error into one shape.
   */
  raw?: unknown;
}

/** Error thrown by the client wrapper for any non-2xx response. */
export class ProblemError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  readonly correlationId: string | null;

  constructor(problem: ProblemDetails, correlationId: string | null) {
    super(problem.title);
    this.name = "ProblemError";
    this.status = problem.status;
    this.problem = problem;
    this.correlationId = correlationId;
  }

  /** Message for the UI. Falls back to the title when there is no detail. */
  get userMessage(): string {
    return this.problem.detail ?? this.problem.title;
  }
}

const UNKNOWN_PROBLEM: Omit<ProblemDetails, "status"> = {
  type: "about:blank",
  title: "Unerwarteter Fehler",
  detail: "Die Anfrage konnte nicht verarbeitet werden.",
};

/**
 * The title this app shows for a status FastAPI never puts a `title` on —
 * every plain `HTTPException(status, "…")` on the way in, one status to one
 * German word, so a screen reading `problem.title` says something a person
 * chose rather than "Unerwarteter Fehler" for a 404 that was never a
 * surprise.
 */
const GERMAN_TITLE_BY_STATUS: Record<number, string> = {
  400: "Ungültige Anfrage",
  401: "Nicht angemeldet",
  404: "Nicht gefunden",
  409: "Konflikt",
  413: "Zu groß",
  422: "Nicht verarbeitbar",
};

function germanTitleFor(status: number): string {
  return GERMAN_TITLE_BY_STATUS[status] ?? "Fehler";
}

/** FastAPI's own 422 body for a request validation error: `detail` is the
 * list of field violations, not a sentence — nothing here is fit to show. */
const VALIDATION_DETAIL = "Die Eingaben entsprechen nicht der erwarteten Form.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Coerces any error payload into problem details — the server may fail before its own error handler. */
export function toProblemDetails(payload: unknown, status: number): ProblemDetails {
  if (isRecord(payload) && "title" in payload) {
    const candidate = payload as Partial<ProblemDetails>;
    return {
      type: candidate.type ?? "about:blank",
      title: candidate.title ?? UNKNOWN_PROBLEM.title,
      status: candidate.status ?? status,
      detail: candidate.detail,
      instance: candidate.instance,
      errors: candidate.errors,
      raw: payload,
    };
  }
  // FastAPI's own `HTTPException(status, "…")` shape — every 4xx the new
  // `/cases` routes raise this way (workflow/router.py, intake/router.py).
  // No `title` ever arrives with it, so one is derived from the status
  // rather than left to read as "Unerwarteter Fehler".
  if (isRecord(payload) && typeof payload.detail === "string") {
    return {
      type: "about:blank",
      title: germanTitleFor(status),
      status,
      detail: payload.detail,
      raw: payload,
    };
  }
  // FastAPI's own request-validation 422 — `detail` is an array of field
  // violations, not a sentence for a screen to show verbatim.
  if (isRecord(payload) && Array.isArray(payload.detail)) {
    return {
      type: "about:blank",
      title: germanTitleFor(status),
      status,
      detail: VALIDATION_DETAIL,
      raw: payload,
    };
  }
  return { ...UNKNOWN_PROBLEM, status, raw: payload };
}
